import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { authAxios } from '../contexts/AuthContext';
import { Package, ChevronRight, Clock, Truck, Check, X, RotateCcw } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../contexts/AuthContext';
import { t } from '../lib/translations';
import Pagination from '../components/Pagination';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusConfig = {
  pending: { icon: Clock, color: 'bg-yellow-100 text-yellow-800', label: 'Pending' },
  confirmed: { icon: Check, color: 'bg-blue-100 text-blue-800', label: 'Confirmed' },
  processing: { icon: Package, color: 'bg-purple-100 text-purple-800', label: 'Processing' },
  shipped: { icon: Truck, color: 'bg-indigo-100 text-indigo-800', label: 'Shipped' },
  delivered: { icon: Check, color: 'bg-green-100 text-green-800', label: 'Delivered' },
  cancelled: { icon: X, color: 'bg-red-100 text-red-800', label: 'Cancelled' },
  returned: { icon: RotateCcw, color: 'bg-gray-100 text-gray-800', label: 'Returned' }
};

const returnStatusConfig = {
  return_requested: { icon: RotateCcw, color: 'bg-yellow-100 text-yellow-800', label: 'Return Requested' },
  return_approved: { icon: Check, color: 'bg-blue-100 text-blue-800', label: 'Return Approved' },
  return_product_picked_up: { icon: Truck, color: 'bg-purple-100 text-purple-800', label: 'Product Picked Up' },
  return_quality_check: { icon: Package, color: 'bg-indigo-100 text-indigo-800', label: 'Quality Check' },
  return_refund_processed: { icon: Check, color: 'bg-green-100 text-green-800', label: 'Refund Processed' },
  return_rejected: { icon: X, color: 'bg-red-100 text-red-800', label: 'Return Rejected' }
};

const ReturnStatusBadge = ({ returnStatus }) => {
  const rs = returnStatusConfig[returnStatus];
  const RSIcon = rs?.icon || RotateCcw;
  return (
    <Badge className={`${rs?.color || 'bg-orange-100 text-orange-800'} flex items-center gap-1`}>
      <RSIcon className="w-3 h-3" />
      {rs?.label || returnStatus}
    </Badge>
  );
};

export default function OrdersPage() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login', { state: { from: { pathname: '/orders' } } });
      return;
    }

    const fetchOrders = async () => {
      setLoading(true);
      try {
        const response = await authAxios.get(`${API}/orders?page=${currentPage}&page_size=20`);
        const data = response.data;
        setOrders(data.orders || data);
        setTotalPages(data.total_pages || 1);
        setTotal(data.total || 0);
      } catch (error) {
        console.error('Failed to fetch orders:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, [isAuthenticated, navigate, currentPage]);

  // Scroll to top when page changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentPage]);

  const formatPrice = (price) => `₹${price.toFixed(2)}`;
  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-green-600 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-8" data-testid="orders-page">
      <div className="container-app">
        <h1 className="text-2xl font-bold text-slate-900 mb-6">{t('orderHistory')}</h1>

        {orders.length === 0 ? (
          <div className="bg-white rounded-xl p-12 text-center">
            <Package className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-slate-900 mb-2">No orders yet</h2>
            <p className="text-slate-500 mb-6">You haven't placed any orders yet.</p>
            <Link to="/products">
              <button className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors">
                {t('shopNow')}
              </button>
            </Link>
          </div>
        ) : (
          <>
            <div className="space-y-4">
              {orders.map((order) => {
                const status = statusConfig[order.status] || statusConfig.pending;
                const StatusIcon = status.icon;

                return (
                  <Link
                    key={order.id}
                    to={`/order/${order.id}`}
                    className="block bg-white rounded-xl p-4 sm:p-6 shadow-sm border border-slate-100 hover:shadow-md transition-shadow"
                    data-testid={`order-${order.id}`}
                  >
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2 flex-wrap">
                          <Badge className={`${status.color} flex items-center gap-1`}>
                            <StatusIcon className="w-3 h-3" />
                            {status.label}
                          </Badge>
                          {order.return_status && (
                            <ReturnStatusBadge returnStatus={order.return_status} />
                          )}
                          <span className="text-sm text-slate-500">
                            {formatDate(order.created_at)}
                          </span>
                        </div>

                        <p className="text-sm text-slate-500 mb-2">
                          Order ID: <span className="font-mono text-xs">{order.id.slice(0, 8)}...</span>
                        </p>

                        <div className="flex items-center gap-2 flex-wrap">
                          {order.items.slice(0, 3).map((item, idx) => (
                            <img
                              key={idx}
                              src={item.image}
                              alt={item.name}
                              className="w-10 h-10 rounded-lg object-cover border border-slate-100"
                            />
                          ))}
                          {order.items.length > 3 && (
                            <span className="text-sm text-slate-500">
                              +{order.items.length - 3} more
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center justify-between sm:flex-col sm:items-end gap-2">
                        <span className="text-lg font-bold text-green-600">
                          {formatPrice(order.total)}
                        </span>
                        <ChevronRight className="w-5 h-5 text-slate-400" />
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>

            {/* Pagination */}
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
            />
          </>
        )}
      </div>
    </div>
  );
}
